@file:OptIn(ExperimentalMaterial3Api::class)

package ui.widget

import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.*
import androidx.compose.ui.graphics.vector.*
import androidx.compose.ui.unit.*
import androidx.compose.ui.util.*
import compose.assets.*
import compose.assets.svg.*

interface DropDownItem {
    val label: String
    val image: ImageVector?
}

@Composable
fun <T : DropDownItem> DropDownPicker(
    selected: MutableState<T>,
    values: List<T>,
    itemWidth: Dp,
    cardModifier: Modifier = Modifier.width(itemWidth),
    modifier: Modifier = Modifier,
    previewImage: ImageVector? = null,
    previewLabel: String? = null,
    enabled: Boolean = true,
    colors: CardColors = CardDefaults.cardColors(),
    iconSize: Dp = Dp.Unspecified,
    useIconTint: Boolean = true,
    outlineCard: Boolean = false,
    onChange: (T) -> Unit = {}
) {
    var isExpanded by remember {
        mutableStateOf(false)
    }
    val animation by animateFloatAsState(
        animationSpec = tween(200),
        targetValue = if (isExpanded) 180F
        else 0F
    )

    val cardContent: @Composable ColumnScope.() -> Unit = {
        Row(Modifier.padding(12.dp)) {

            previewLabel?.also {
                Text(
                    text = it,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                Space(16.dp)
            }

            (selected.value.image ?: previewImage)?.also {
                if (useIconTint) {
                    Icon(
                        imageVector = it,
                        contentDescription = null,
                        modifier = Modifier.align(Alignment.CenterVertically)
                            .let {
                                if (iconSize == Dp.Unspecified) it
                                else it.size(iconSize)
                            }
                    )
                } else {
                    Image(
                        imageVector = it,
                        contentDescription = null,
                        modifier = Modifier.align(Alignment.CenterVertically)
                            .let {
                                if (iconSize == Dp.Unspecified) it.clip(CircleShape)
                                else it.size(iconSize).clip(CircleShape)
                            }
                    )
                }
                Space(16.dp)
            }

            Text(
                text = selected.value.label,
                modifier = Modifier.weight(1F)
                    .align(Alignment.CenterVertically)
            )
            Icon(
                imageVector = Svg.Chevron_down,
                contentDescription = null,
                modifier = Modifier
                    .align(Alignment.CenterVertically)
                    .rotate(animation)
            )
        }
    }
    Box(modifier) {
        if (outlineCard) {
            OutlinedCard(
                onClick = { isExpanded = !isExpanded },
                enabled = enabled,
                colors = colors,
                modifier = cardModifier.clip(CardDefaults.shape)
            ) {
                cardContent()
            }
        } else {
            Card(
                onClick = { isExpanded = !isExpanded },
                enabled = enabled,
                colors = colors,
                modifier = cardModifier.clip(CardDefaults.shape)
            ) {
                cardContent()
            }
        }
        DropdownMenu(
            expanded = isExpanded,
            modifier = Modifier.clip(CardDefaults.shape),
            onDismissRequest = { isExpanded = false }) {
            values.fastForEach { value ->
                DropdownMenuItem(
                    modifier = Modifier.width(itemWidth),
                    text = {
                        Row {
                            value.image?.also {
                                if (useIconTint) {
                                    Icon(
                                        imageVector = it,
                                        contentDescription = value.label,
                                        modifier =
                                        if (iconSize == Dp.Unspecified) Modifier
                                        else Modifier.size(iconSize)
                                    )
                                } else {
                                    Image(
                                        imageVector = it,
                                        contentDescription = value.label,
                                        modifier =
                                        if (iconSize == Dp.Unspecified) Modifier.clip(CircleShape)
                                        else Modifier.size(iconSize).clip(CircleShape)
                                    )
                                }
                                Space(16.dp)
                            }
                            Text(value.label, Modifier.align(Alignment.CenterVertically))
                        }
                    },
                    onClick = {
                        selected.value = value
                        onChange(value)
                        isExpanded = false
                    })
            }
        }

    }
}
